import { useBlocksConfig } from "@pathwright/blocks-engine"
import { BlockText } from "@pathwright/blocks/src/shared"
import {
  FilestackPreviewModal,
  useMuxEmbed
} from "@pathwright/legacy-media-manager"
import {
  MuxDownloadButton,
  createMediaDownloadURL
} from "@pathwright/media-client"
import { canPreview } from "@pathwright/media-utils"
import Attachment from "@pathwright/ui/src/components/attachment/Attachment"
import { MEDIA_TYPES } from "@pathwright/ux/src/components/filestack/constants"
import {
  getPreviewURL,
  isHeic
} from "@pathwright/ux/src/components/filestack/utils"
import PropTypes from "prop-types"
import { isMobile, removeHTML } from "../../../utils"
import { AttachmentListItemWrapper } from "../ui"

const AttachmentsListItem = (props) => {
  props = {
    mode: "view",
    ...props
  }

  const {
    attachment,
    onUpdateAttachmentName,
    onRemoveAttachment,
    mode,
    domID
  } = props

  const { allowDownloadableVideos } = useBlocksConfig()
  const {
    embed: muxData, // from mux video HOC,
    loading: muxLoading,
    error: muxError
  } = useMuxEmbed() || {}

  if (muxLoading) return null
  if (muxError) {
    console.error(muxError)
    return null
  }

  const handleChangeName = (nextName) => {
    const nextAttachment = {
      ...attachment,
      name: removeHTML(nextName)
    }
    onUpdateAttachmentName(nextAttachment)
  }

  const handleRemove = (attachment) => () => {
    onRemoveAttachment(attachment)
  }

  // Simulate click on attachment to open modal.
  // This handler will not fire if the enter/space key is used to download the attachment
  // since the Attachment handles the download logic and prevents the default behavior.
  function handleAttachmentKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.currentTarget.click()
    }
  }

  const {
    name,
    url,
    thumb,
    mimetype,
    filestackHandle,
    mediaType,
    muxVideoID,
    fileSize
  } = attachment

  // Get thumbnail if available
  const thumbnail = thumb || (mediaType === MEDIA_TYPES.IMAGE ? url : "")

  // Get Mux download url from gql props

  const muxReadyURL = muxData?.download?.url

  // Get download url
  const downloadURL = createMediaDownloadURL(attachment, muxData)

  // Get preview url (default filestack url for certain file types)
  const previewURL =
    canPreview(mimetype) && filestackHandle
      ? getPreviewURL(filestackHandle)
      : null
  const previewImageURL = isHeic(mimetype) ? url : undefined

  const canGenerateDownload = allowDownloadableVideos && !!muxVideoID
  const needsGenerateDownload = !downloadURL && !!muxVideoID

  const renderAttachment = ({ onClickDownload, isLoading } = {}) => {
    return (
      <Attachment
        name={name}
        mimetype={mimetype}
        thumbnail={thumbnail}
        url={downloadURL || url}
        download={!!downloadURL || canGenerateDownload}
        downloadOnly={(!!downloadURL || canGenerateDownload) && !previewURL}
        // If we don't have a download url for mux video,
        // we need to allow it to be generated on click
        onClickDownload={
          needsGenerateDownload
            ? (event) => {
                if (!downloadURL) event.stopPropagation()
                if (onClickDownload) onClickDownload(event)
              }
            : null
        }
        isLink={mediaType === MEDIA_TYPES.LINK}
        // requireConfirmation={false}
        onRemove={mode === "edit" ? handleRemove(attachment) : null}
        truncate={isMobile() ? 20 : 60} // On mobile shorten name gracefully
        mode={mode}
        fileSize={fileSize}
        loadingURL={!!muxVideoID && !muxReadyURL}
        overlay={true}
        isLoadingDownload={isLoading}
        renderNameView={
          mode === "view" ? ({ name }) => <BlockText html={name} /> : null
        }
      />
    )
  }

  const node = (
    <AttachmentListItemWrapper
      isMobile={isMobile()}
      className={`AttachmentListItem${
        previewURL ? " AttachmentListItem--has-preview" : ""
      }`}
      onKeyDown={handleAttachmentKeyDown}
      tabIndex={0}
      id={domID}
    >
      {muxVideoID ? (
        <MuxDownloadButton
          id={muxVideoID}
          downloadURL={downloadURL}
          renderButton={({ onClick, isLoading }) =>
            renderAttachment({ onClickDownload: onClick, isLoading })
          }
        />
      ) : (
        renderAttachment()
      )}
    </AttachmentListItemWrapper>
  )

  return previewURL ? (
    <FilestackPreviewModal
      previewHandle={filestackHandle}
      previewURL={previewImageURL}
      downloadURL={downloadURL}
    >
      {node}
    </FilestackPreviewModal>
  ) : (
    node
  )
}

AttachmentsListItem.propTypes = {
  mode: PropTypes.oneOf(["view", "edit"]),
  onRemoveAttachment: PropTypes.func,
  attachment: PropTypes.object.isRequired,
  domID: PropTypes.string.isRequired
}

export default AttachmentsListItem
